import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import clsx from 'clsx'
import React, { type FormEventHandler, useEffect, useId, useRef, useState } from 'react'
import './ApplyV2Textarea.scss'

const baseClass = 'ApplyV2Textarea'

interface ApplyV2TextareaProps {
    value: string | null | undefined
    label?: React.ReactNode
    rightLabel?: React.ReactNode
    onChange?: (value: string) => void
    // onChange: FormEventHandler<HTMLDivElement>
    onBlur?: React.FocusEventHandler<HTMLElement>
    onFocus?: React.FocusEventHandler<HTMLElement>
    isDisabled?: boolean
    className?: string
    id?: string | number
    name?: string
    isLoading?: boolean
    // isError?: boolean
    errorMessage?: string | null | boolean
    isRequired?: boolean
    placeholder?: string
    maxLength?: number
    minLength?: number
    autoFocus?: boolean
    isShowErrorMessage?: boolean
    description?: string | number | null
}

function ApplyV2Textarea({
    value = '',
    label,
    rightLabel,
    onChange,
    onFocus,
    onBlur,
    isDisabled = false,
    className = '',
    id,
    name,
    isLoading = false,
    errorMessage,
    isRequired = false,
    placeholder = '',
    maxLength,
    minLength,
    autoFocus,
    isShowErrorMessage = true,
    description,
}: ApplyV2TextareaProps) {
    const defaultId = useId()
    const ref = useRef<HTMLDivElement | null>(null)
    const [isEmpty, setIsEmpty] = useState(value === '')

    const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
        const syntheticEvent = {
            ...e,
            target: {
                ...e.target,
                name: name || '',
                value: ref.current?.innerText || '',
            },
        } as React.FocusEvent<HTMLInputElement>

        onBlur?.(syntheticEvent)
    }

    const handleInput = () => {
        const text = ref.current?.innerText || ''
        const isEmp = text.trim() === ''
        setIsEmpty(isEmp)
        onChange?.(isEmp ? '' : text)
    }

    useEffect(() => {
        const div = ref?.current
        if (div && div.innerText !== value) {
            div.innerText = value || ''
        }
        if (!value) {
            setIsEmpty(true)
        } else {
            setIsEmpty(false)
        }
    }, [value])

    useEffect(() => {
        if (autoFocus) {
            ref?.current?.focus()
            setTimeout(() => {
                ref.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest',
                })
            }, 50)
        }
    }, [autoFocus])

    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--error`]: !!errorMessage,
                [`${baseClass}--loading`]: isLoading,
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--empty`]: isEmpty,
            })}
            data-error={!!errorMessage ? 'true' : 'false'}
        >
            {(label || rightLabel) && (
                <label className={`${baseClass}__label`} htmlFor={id ? id.toString() : defaultId}>
                    <span>
                        {label}
                        {isRequired && '*'}
                    </span>
                    {rightLabel && <span>{rightLabel}</span>}
                </label>
            )}
            <div
                className={clsx(`${baseClass}__textarea-wrapper`, {
                    [`${className}__textarea-wrapper`]: className,
                    [`${baseClass}__textarea-wrapper--disabled`]: isDisabled,
                    [`${baseClass}__textarea-wrapper--loading`]: isLoading,
                    [`${baseClass}__textarea-wrapper--error`]: !!errorMessage,
                    [`${className}__textarea-wrapper--disabled`]: className && isDisabled,
                    [`${className}__textarea-wrapper--loading`]: className && isLoading,
                    [`${className}__textarea-wrapper--error`]: className && !!errorMessage,
                })}
            >
                <div
                    className={clsx(`${baseClass}__textarea`, {
                        [`${baseClass}__textarea--error`]: !!errorMessage,
                        [`${baseClass}__textarea--loading`]: isLoading,
                        [`${className}__textarea--error`]: className && !!errorMessage,
                        [`${className}__textarea--loading`]: className && isLoading,
                    })}
                    ref={ref}
                    id={id ? id.toString() : defaultId}
                    // name={name}
                    contentEditable={isDisabled ? 'false' : 'plaintext-only'}
                    onInput={handleInput}
                    onFocus={(e) => {
                        console.log('focus')
                        onFocus?.(e)
                    }}
                    onBlur={handleBlur}
                    suppressContentEditableWarning
                    tabIndex={0}
                />
                {isEmpty && (
                    <div
                        className={clsx(`${baseClass}--placeholder`, {
                            [`${className}--placeholder`]: className,
                        })}
                    >
                        {placeholder}
                    </div>
                )}
                {isLoading && (
                    <div className={`${baseClass}__loading-indicator`}>
                        <div className={`${baseClass}__spinner`}></div>
                    </div>
                )}
            </div>
            {description && (
                <div
                    className={clsx(`${baseClass}__description`, {
                        [`${className}__description`]: className,
                    })}
                >
                    {description}
                </div>
            )}
            {errorMessage && isShowErrorMessage && (
                <div
                    className={clsx(`${baseClass}__error`, {
                        [`${className}__error`]: className,
                    })}
                >
                    <div
                        className={clsx(`${baseClass}__error--icon`, {
                            [`${className}__error--icon`]: className,
                        })}
                    >
                        <WarningErrorIcon />
                    </div>
                    <div
                        className={clsx(`${baseClass}__error--text`, {
                            [`${className}__error--text`]: className,
                        })}
                    >
                        {errorMessage}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ApplyV2Textarea
